import React, { FC, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton, Link, Typography } from '@mui/material';
import { Button } from 'src/components/Button';
import {
  ButtonsContainer,
  Card,
  CardHeader,
  Container,
  IconsContainer,
} from 'src/components/ConnectorCard/styled';
import { Modal } from 'src/components/Modals';
import {
  CHATGPT_KIPIFY_PLUGIN_URL,
  CLAUDE_MCP_SETUP_DOCS_URL,
  KIPIFY_MCP_SERVER_URL,
} from 'src/constants/mcp';
import { SubTitle } from 'src/pages/Connectors/styled';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';

/** Same footprint as other connector icons (40×40), flat mark — no robot. */
const McpConnectorIcon: FC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
    <rect width="40" height="40" rx="8" fill="#222822" />
    <path
      d="M14.5 20h11M17 16.5h6M17 23.5h6"
      stroke="#1EED9F"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
    <circle cx="14.5" cy="20" r="2.25" fill="#1EED9F" />
    <circle cx="25.5" cy="20" r="2.25" fill="#1EED9F" />
  </svg>
);

const StepTitle: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="h3"
    sx={{
      fontWeight: 700,
      fontSize: '15px',
      lineHeight: 1.35,
      mt: 2.5,
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const Body: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="p"
    sx={{
      fontSize: '14px',
      lineHeight: 1.5,
      color: 'rgba(34, 40, 34, 0.85)',
      m: 0,
    }}
  >
    {children}
  </Typography>
);

const BulletList: FC<{ items: string[] }> = ({ items }) => (
  <Box
    component="ul"
    sx={{
      m: '8px 0 0',
      pl: 2.25,
      '& li': {
        fontSize: '14px',
        lineHeight: 1.45,
        color: 'rgba(34, 40, 34, 0.85)',
        mb: 0.5,
      },
    }}
  >
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </Box>
);

export const McpConnectorSection: FC = () => {
  const [guideOpen, setGuideOpen] = useState(false);
  const snackbar = useSnackbar();

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(KIPIFY_MCP_SERVER_URL);
      snackbar('MCP server URL copied', 'success');
    } catch {
      snackbar('Could not copy. Select the URL manually.', 'warning');
    }
  };

  return (
    <Box>
      <SubTitle sx={{ mb: '10px' }}>AI assistants</SubTitle>

      <Card>
        <Container>
          <CardHeader>
            <IconsContainer>
              <McpConnectorIcon />
            </IconsContainer>
            <Box>
              <Typography variant="body1" fontWeight={600}>
                Kipify MCP
              </Typography>
              <Typography variant="body2">
                Claude connectors · ChatGPT official plugin
              </Typography>
            </Box>
          </CardHeader>
        </Container>

        <ButtonsContainer>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={() => setGuideOpen(true)}
          >
            How to connect
          </Button>
        </ButtonsContainer>
      </Card>

      <Modal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        title="Connect Kipify MCP"
      >
        <Box sx={{ maxWidth: 560, pb: 1 }}>
          <Body>
            Kipify MCP lets Claude and ChatGPT query the same data you already
            connected in Kipify (Shopify, Magento, Google Ads, Meta Ads, GA4,
            and more), using your Kipify login and permissions.
          </Body>

          <StepTitle>1. Prerequisites</StepTitle>
          <BulletList
            items={[
              'A Kipify account with at least one data connector set up.',
              'Accounts / properties selected in Kipify where required (Ads, GA4, etc.).',
              'Claude (custom connectors) or ChatGPT (official Kipify plugin).',
            ]}
          />

          <StepTitle>2. Connect in ChatGPT (official plugin)</StepTitle>
          <Body>
            Prefer the official <strong>Kipify</strong> plugin from the ChatGPT
            Plugins directory — no custom MCP server URL needed.
          </Body>
          <BulletList
            items={[
              'Open the Kipify plugin page (link below), or ChatGPT → Plugins and search for Kipify.',
              'Add / install the Kipify plugin.',
              'Complete the Kipify sign-in page and select which data sources to share.',
              'Start a new chat and ask about your connected marketing or ecommerce data.',
            ]}
          />
          <Link
            href={CHATGPT_KIPIFY_PLUGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Open Kipify in ChatGPT Plugins
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </Link>

          <StepTitle>3. Kipify MCP server URL (Claude)</StepTitle>
          <Body>
            Claude still uses a custom remote MCP connector. Paste this URL when
            Claude asks for your MCP server:
          </Body>
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              p: '10px 12px',
              borderRadius: '6px',
              backgroundColor: 'rgba(239, 239, 244, 1)',
              border: '1px solid rgba(221, 221, 234, 1)',
            }}
          >
            <Typography
              component="code"
              sx={{
                flex: 1,
                fontSize: '13px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                wordBreak: 'break-all',
              }}
            >
              {KIPIFY_MCP_SERVER_URL}
            </Typography>
            <IconButton
              aria-label="Copy MCP server URL"
              onClick={copyUrl}
              size="small"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>

          <StepTitle>4. Connect in Claude</StepTitle>
          <BulletList
            items={[
              'Open Claude → Customize (or Settings) → Connectors.',
              'Add a custom connector and paste the Kipify MCP URL above.',
              'Complete the Kipify sign-in and select which data sources to share.',
            ]}
          />
          <Link
            href={CLAUDE_MCP_SETUP_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Claude: custom connectors with remote MCP
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </Link>

          <StepTitle>5. After you authorize</StepTitle>
          <BulletList
            items={[
              'Ask the assistant about performance, campaigns, or catalog data in natural language.',
              'It can only use sources you allowed during authorize (tick Google Search Console when present).',
              'If tools look empty, reconnect Kipify connectors and re-authorize with the right scopes.',
              'If ChatGPT is missing new tools after a Kipify update, remove the Kipify plugin, add it again from the official listing, then start a new chat.',
            ]}
          />

          <Typography
            sx={{
              mt: 2.5,
              fontSize: '12px',
              lineHeight: 1.4,
              color: 'rgba(34, 40, 34, 0.55)',
            }}
          >
            ChatGPT uses the official Kipify plugin; Claude uses custom
            Connectors with the remote MCP URL above.
          </Typography>

          <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setGuideOpen(false)}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
